<template>
  <Tooltip placement="top">
    <template #trigger>
      <button
        class="button"
        :data-test-id="`BaseEditorButton-${componentName}`"
        :class="{ active: active, disabled: disabled }"
        @click="handleAction"
      >
        <i class="icon">{{ icon }}</i>
        <input
          v-if="isColor"
          ref="colorInput"
          class="color-input"
          type="color"
          :data-test-id="`BaseEditorColorInput-${componentName}`"
          @input="handleColorAction"
        >
        <input
          v-if="isImage"
          ref="imageInput"
          class="image-input"
          type="file"
          accept="image/png, image/jpeg"
          :data-test-id="`BaseEditorImageInput-${componentName}`"
          @change="handleFileAction"
        >
      </button>
    </template>
    {{ tooltip }}
  </Tooltip>
</template>

<script lang="ts" setup>
  import { computed, ref, toRef } from 'vue'

  import { Tooltip } from '@/components/ui'
  import { dietsService } from '@/services/dietsService'
  import { useGlobalStore } from '@/store/globalStore'

  const props = defineProps({
    active: {
      default: false,
      type: Boolean,
    },
    componentName: {
      required: true,
      type: String,
    },
    disabled: {
      default: false,
      type: Boolean,
    },
    icon: {
      required: false,
      type: String,
    },
    tooltip: {
      required: true,
      type: String,
    },
    type: {
      required: false,
      type: String,
    },
  })
  const { createMessage } = useGlobalStore()
  const emit = defineEmits(['action'])
  const propType = toRef(props, 'type')
  const colorInput = ref<HTMLInputElement>()
  const imageInput = ref<HTMLInputElement>()
  const isColor = computed(() => propType.value === 'color')
  const isImage = computed(() => propType.value === 'image')

  const handleAction = (event: Event) => {
    if (isColor.value) {
      colorInput.value?.click()
    } else if (isImage.value) {
      imageInput.value?.click()
    } else {
      emit('action', event)
    }
  }
  const handleColorAction = (event: Event) => {
    emit('action', event)
  }
  const handleFileAction = async (event: Event) => {
    const file = (event.target as HTMLInputElement).files?.[0]
    if (file) {
      const response = await dietsService.commentImageUpload(file)
      if (response.data.url) {
        emit('action', response.data.url)
      } else if (response.data.error) {
        createMessage({ title: response.data.error })
      } else {
        throw new Error('Wystąpił nieoczekiwany błąd podczas wgrywania pliku.')
      }
    }
  }
</script>

<style scoped>
  .button {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    cursor: pointer;
    background: transparent;
    border: 0;
    border-radius: 4px;
  }

  .button:hover,
  .button.active {
    background: #f7f7f7;
  }

  .button.disabled {
    pointer-events: none;
    background: transparent;
  }

  .button .icon {
    display: inline-flex;
    font-family: "Material Icons";
    font-size: 20px; /* Preferred icon size */
    font-style: normal;
    font-weight: normal;

    /* Support for IE. */
    font-feature-settings: "liga";

    /* Support for all WebKit browsers. */
    -webkit-font-smoothing: antialiased;

    /* Support for Firefox. */
    -moz-osx-font-smoothing: grayscale;
    line-height: 1;
    vertical-align: top;
    text-transform: none;
    letter-spacing: normal;
    overflow-wrap: normal;
    white-space: nowrap;
    cursor: default;
    direction: ltr;

    /* Support for Safari and Chrome. */
    text-rendering: optimizelegibility;
  }

  .button:hover .icon {
    color: var(--color-primary-text);
  }

  .button.active .icon {
    color: var(--color-main-100);
  }

  .button.disabled .icon {
    color: #ccc;
  }

  .color-input {
    position: absolute;
    top: 0;
    left: 0;
    visibility: hidden;
  }

  .image-input {
    display: none;
  }
</style>
